/** Avatar color rules used across portal.
 * Each avatar consumer (summary card, recommended-times card, current-repair
 * card, calendar advisor button, calendar tab) picks a color from
 * {@link AvatarColor} based on the active OEM theme, and for the calendar
 * surfaces, whether the advisor is selected and whether an advisor image is
 * available.
 */
import { THEME } from 'src/constants/ThemeNames';

/** The color tokens accepted by `Avatar` from `@framework-controls/core`. */
export type AvatarColor = 'default' | 'primary' | 'secondary' | 'contrast' | 'completed' | 'blank';

/**
 * Resolves the advisor avatar image source: the provided URL when present,
 * otherwise the supplied placeholder. Centralizes the "URL or fallback" check
 * that each avatar consumer previously duplicated.
 * @param url - the advisor image URL (may be empty or undefined)
 * @param fallback - the placeholder image to use when no URL is present
 */
export const getAdvisorImage = (url:string | undefined, fallback: string): string => url || fallback;
/** Avatar color for the appointment summary card. */
export const getAppointmentAvatarColor = (theme: string): AvatarColor => {
    let avatarColor: AvatarColor = 'primary';
    if (theme === THEME.MERCEDES_BENZ){
        avatarColor = 'contrast';
    }

    return avatarColor;
};
/** Avatar color for the QuickTime (recommended times) card. */
export const getQuickTimeAvatarColor = (theme: string): AvatarColor => {
    let avatarColor: AvatarColor = 'primary';
    if (theme === THEME.LEXUS || theme === THEME.TOYOTA_SPM || theme === THEME.AUDI || theme === THEME.PORSCHE ||
        theme === THEME.FORD || theme === THEME.INFINITI || theme === THEME.MERCEDES_BENZ) {
        avatarColor = 'contrast';
    }
    if (theme === THEME.KIA || theme === THEME.HYUNDAI){
        avatarColor = 'default';
    }
    if (theme === THEME.BMW || theme === THEME.SUBARU || theme === THEME.NISSAN || theme === THEME.MAZDA ||
        theme === THEME.ACURA || theme === THEME.GENESIS){
        avatarColor = 'blank';
    }

    return avatarColor;
};

/** Avatar color for the current-repair advisor card. */
export const getRepairAdvisorAvatarColor = (theme: string): AvatarColor => {
    let avatarColor: AvatarColor = 'primary';
    if (theme === THEME.PORSCHE || theme === THEME.FORD || theme === THEME.INFINITI ||
        theme === THEME.MERCEDES_BENZ){
        avatarColor = 'contrast';
    }
    if (theme === THEME.CHEVROLET || theme === THEME.BMW || theme === THEME.SUBARU || theme === THEME.NISSAN ||
        theme === THEME.MAZDA || theme === THEME.ACURA || theme === THEME.GENESIS || theme === THEME.LEXUS){
        avatarColor = 'blank';
    }
    if (theme === THEME.KIA || theme === THEME.HYUNDAI){
        avatarColor = 'default';
    }

    return avatarColor;
};

/** Avatar color for the calendar advisor-selection panel. */
export const getCalendarAdvPanelAvatarColor = (
    theme: string,
    selected: boolean,
    hasAdvisorImage: boolean,
): AvatarColor => {
    let avatarColor: AvatarColor = 'blank';
    if (theme === THEME.TOYOTA_SPM || theme === THEME.INFINITI) {
        avatarColor = 'contrast';
    }
    if (!hasAdvisorImage && selected && theme === THEME.TOYOTA_SPM){
        avatarColor = 'primary';
    }

    return avatarColor;
};

/** Avatar color for the calendar advisor tab button. */
export const getCalendarSelectBtnAvatarColor = (
    theme: string,
    selected: boolean,
    hasAdvisorURL: boolean,
): AvatarColor => {
    let avatarColor: AvatarColor = 'blank';
    if (theme === THEME.COLOR_SENSITIVE){
        avatarColor = 'primary';
    }
    if (!selected && theme === THEME.TOYOTA_SPM){
        avatarColor = 'contrast';
    }
    if (!hasAdvisorURL){
        if (selected && theme === THEME.TOYOTA_SPM){
            avatarColor = 'primary';
        }
    }
    if (theme === THEME.INFINITI){
        avatarColor = 'contrast';
    }

    return avatarColor;
};
